import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Users, Calendar, Clock, CheckCircle, XCircle, BarChart2, LogOut } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { admin as adminApi, type Booking } from '../lib/api'

const STATUS_COLORS: Record<string, string> = {
  pending: '#c8a96e',
  reviewing: '#7a9ec8',
  confirmed: '#9ec87a',
  declined: '#c87a7a',
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pending: <Clock size={12} />,
  reviewing: <BarChart2 size={12} />,
  confirmed: <CheckCircle size={12} />,
  declined: <XCircle size={12} />,
}

export default function AdminDashboard() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()

  const [stats, setStats] = useState<{
    totalUsers: number
    totalBookings: number
    bookingsByStatus: Record<string, number>
    recentBookings: Booking[]
  } | null>(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    adminApi.getStats()
      .then(setStats)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [])

  async function handleSignOut() {
    await signOut()
    navigate('/')
  }

  const cell: React.CSSProperties = {
    background: 'var(--ink-2)',
    border: '1px solid var(--border)',
    padding: '24px',
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--ink)', display: 'flex', flexDirection: 'column' }}>
      {/* Admin nav */}
      <header style={{ background: 'rgba(13,12,10,0.97)', borderBottom: '1px solid var(--border)', padding: '0 24px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <Link to="/" style={{ fontFamily: 'var(--font-display)', fontSize: '14px', letterSpacing: '4px', color: 'var(--gold)', textDecoration: 'none', textTransform: 'uppercase' }}>
            DJ 30Rack
          </Link>
          <span style={{ color: 'var(--border)' }}>|</span>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '11px', letterSpacing: '3px', color: 'var(--muted)', textTransform: 'uppercase' }}>
            Admin Panel
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '12px', color: 'var(--muted)' }}>{user?.email}</span>
          <button onClick={handleSignOut} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontFamily: 'var(--font-display)', letterSpacing: '2px', textTransform: 'uppercase' }}>
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </header>

      {/* Sidebar + main layout */}
      <div style={{ display: 'flex', flex: 1 }}>
        {/* Sidebar */}
        <nav style={{ width: '200px', background: 'var(--ink-2)', borderRight: '1px solid var(--border)', padding: '24px 0', flexShrink: 0 }}>
          {[
            { label: 'Dashboard', href: '/admin', icon: <BarChart2 size={14} /> },
            { label: 'Bookings', href: '/admin/bookings', icon: <Calendar size={14} /> },
            { label: 'Users', href: '/admin/users', icon: <Users size={14} /> },
          ].map((item) => (
            <Link key={item.href} to={item.href}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', fontFamily: 'var(--font-display)', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(242,236,224,0.5)', textDecoration: 'none', transition: 'color 0.15s' }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'var(--gold)')}
              onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(242,236,224,0.5)')}
            >
              {item.icon}{item.label}
            </Link>
          ))}
        </nav>

        {/* Main content */}
        <main style={{ flex: 1, padding: '32px', overflowY: 'auto' }}>
          <div style={{ marginBottom: '32px' }}>
            <div className="section-label">Overview</div>
            <h1 className="section-title" style={{ fontSize: '32px' }}>Dashboard</h1>
          </div>

          {error && (
            <div style={{ color: '#e08080', background: 'rgba(200,50,50,0.1)', border: '1px solid rgba(200,50,50,0.3)', padding: '12px 16px', marginBottom: '24px' }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ color: 'var(--muted)', fontFamily: 'var(--font-display)', letterSpacing: '3px', fontSize: '11px', textTransform: 'uppercase' }}>
              Loading...
            </div>
          ) : stats && (
            <>
              {/* Stat cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1px', background: 'var(--border)', marginBottom: '32px' }}>
                <div style={cell}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: '8px' }}>Total Bookings</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '40px', fontWeight: 700, color: 'var(--gold)', lineHeight: 1 }}>{stats.totalBookings}</div>
                </div>
                <div style={cell}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: '8px' }}>Fan Members</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '40px', fontWeight: 700, color: 'var(--gold)', lineHeight: 1 }}>{stats.totalUsers}</div>
                </div>
                {Object.entries(stats.bookingsByStatus).map(([status, count]) => (
                  <div key={status} style={cell}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', color: STATUS_COLORS[status] ?? 'var(--muted)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {STATUS_ICONS[status]}{status}
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '40px', fontWeight: 700, color: 'var(--cream)', lineHeight: 1 }}>{count}</div>
                  </div>
                ))}
              </div>

              {/* Recent bookings */}
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '16px', letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--cream)' }}>Recent Bookings</h2>
                <Link to="/admin/bookings" style={{ fontFamily: 'var(--font-display)', fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gold-dim)', textDecoration: 'none' }}>
                  View All →
                </Link>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--border)' }}>
                {stats.recentBookings.length === 0 && (
                  <div style={{ background: 'var(--ink-2)', padding: '24px', color: 'var(--muted)', fontFamily: 'var(--font-display)', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase' }}>
                    No bookings yet
                  </div>
                )}
                {stats.recentBookings.map((b) => (
                  <Link key={b.id} to={`/admin/bookings`}
                    style={{ display: 'grid', gridTemplateColumns: '1fr 120px 100px 120px', gap: '16px', alignItems: 'center', background: 'var(--ink-2)', padding: '14px 20px', textDecoration: 'none', transition: 'background 0.15s' }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = '#1a1710')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = 'var(--ink-2)')}
                  >
                    <div>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '13px', color: 'var(--cream)', letterSpacing: '0.5px' }}>{b.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{b.email}</div>
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--muted)' }}>{b.event_type}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontFamily: 'var(--font-display)', fontSize: '9px', letterSpacing: '2px', textTransform: 'uppercase', color: STATUS_COLORS[b.status] ?? 'var(--muted)' }}>
                      {STATUS_ICONS[b.status]}{b.status}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      {new Date(b.created_at).toLocaleDateString()}
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  )
}
